// A project task's drawer (Block J, UI-22: "Projects follows the Team board"): the kit's SidePanel,
// full screen on a phone, like the Team dashboard's task drawer.
//
// Block J fixes, each found while restyling:
//   - Status offered 'in progress' and 'completed', values nothing reads (the data holds
//     'in_progress' and 'done', Block 216), so a task moved here vanished from the board, and the
//     control could not show the value a task actually had. Subtasks were ticked off the same way.
//   - Every change was sent without waiting (projectManager.updateProjectTask only logs a failure),
//     so a refused change sat on screen as saved. Each is now awaited; a refused one goes back and the
//     server's own words show here, beside the controls.
//
// Decision 3 (2026-10-12): the Files tab keeps uploaded files in the one private file store (kind
// 'project_task', /api/files: upload, list, a short-lived link to open, delete). It called
// /api/tasks/:id/attachments with the project task's id, which names a task in a different table.
// Links (Google Drive, OneDrive, the web) are kept on the project task itself (its attachments).
import React, { useState, useEffect, useRef } from 'react';
import { describeWriteFailure } from '../../utils/writeFailure';
import projectManager from '../../models/projects';
import { useWorkspaceRoster, useWorkspaceRosterMap } from '../../hooks/useWorkspaceRoster';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { api } from '../../api/client';
import { messageFromError } from '../../utils/apiError';
import { formatDate } from '../../utils/formatting';
import {
  Avatar, Button, Checkbox, DateInput, EmptyState, ErrorState, Field, Input, SegmentedControl, Select, SidePanel, StatTile, StatusPill,
  TabGroup, Textarea, Reveal,
} from '../../ui';
import { personTint } from '../Team/teamPeople';
import { STATUS_LABEL, STATUS_TINT, TASK_STATUSES, formatMinutes, minutesOf } from '../Projects/ProjectViews';
import '../Projects/projects.css';

const PRIORITY_CHOICES = [
  { value: 'Low', label: 'Low', color: 'priority-low' },
  { value: 'Medium', label: 'Medium', color: 'priority-medium' },
  { value: 'High', label: 'High', color: 'priority-high', dot: true },   // decision 50: High has the dot
];
const SOURCE_LABEL = { google_drive: 'Google Drive', onedrive: 'OneDrive', url: 'Link' };
// What the file store takes (lib/fileStore ALLOWED): offered by the picker, enforced by the server.
const FILE_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.ppt,.pptx';
const isLink = (a) => !!a && !!SOURCE_LABEL[a.source] && typeof a.externalUrl === 'string';
const sizeText = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round((bytes || 0) / 1024))} KB`);
const when = (entry) => formatDate(entry?.timestamp || entry?.date, undefined, { day: 'numeric', month: 'short', year: 'numeric' }) || '';

function FileIcon({ source, mimeType }) {
  let icon = '📎';
  if (source === 'google_drive' || source === 'onedrive' || source === 'url') icon = '🔗';
  else if (mimeType?.startsWith('image/')) icon = '🖼️';
  else if (mimeType === 'application/pdf') icon = '📄';
  return <span className="pj-file__icon" aria-hidden="true">{icon}</span>;
}

export default function TaskDetailPanel({ taskId, onClose, onUpdated }) {
  const { currentUser } = useCurrentUser();
  // Roster hooks must run before any early return (rules-of-hooks).
  const { members: allUsers } = useWorkspaceRoster();
  const rosterMap = useWorkspaceRosterMap();
  const [task, setTask] = useState(null);
  const [tab, setTab] = useState('details');
  const [titleDraft, setTitleDraft] = useState('');
  const [descDraft, setDescDraft] = useState('');
  const [pointsDraft, setPointsDraft] = useState('');
  const [failure, setFailure] = useState('');
  const [busy, setBusy] = useState('');

  // Comments
  const [commentText, setCommentText] = useState('');

  // Time log
  const [logMins, setLogMins] = useState('');
  const [logNote, setLogNote] = useState('');

  // Subtasks
  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [subtasks, setSubtasks] = useState([]);

  // Files: stored files (the file store) and links (on the task)
  const [files, setFiles] = useState([]);
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [filesLoadFailure, setFilesLoadFailure] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [linkingFile, setLinkingFile] = useState(false);
  const [linkSource, setLinkSource] = useState('google_drive');
  const [linkName, setLinkName] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);

  // Tags
  const [tagInput, setTagInput] = useState('');

  const reload = () => {
    if (!taskId) return;
    const t = projectManager.getTaskById(taskId);
    if (t) {
      setTask({ ...t });
      setSubtasks(projectManager.getSubtasks(taskId));
    }
  };

  useEffect(() => {
    if (!taskId) { setTask(null); return; }
    setTab('details');
    setFailure('');
    reload();
    const t = projectManager.getTaskById(taskId);
    setTitleDraft(t?.title || '');
    setDescDraft(t?.description || '');
    setPointsDraft(t?.storyPoints ? String(t.storyPoints) : '');
    loadFiles();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadFiles() {
    setFilesLoadFailure('');
    try {
      const res = await api(`/api/files?kind=project_task&entityId=${encodeURIComponent(taskId)}`);
      const data = await res.json();
      setFiles(Array.isArray(data?.files) ? data.files : []);
      setFilesLoaded(true);
    } catch (err) {
      // Said where it is seen (the Files tab), and never as "No files yet".
      setFilesLoadFailure(messageFromError(err, "This task's files could not be loaded."));
    }
  }

  const [fileFailure, setFileFailure] = useState('');

  // One file, uploaded to the file store against this task. What shows is what the server stored.
  async function uploadFile(file) {
    if (!file) return;
    setUploading(true);
    setFileFailure('');
    try {
      const body = new FormData();
      body.append('kind', 'project_task');
      body.append('entityId', String(taskId));
      body.append('file', file);
      const res = await api('/api/files', { method: 'POST', body });
      const data = await res.json();
      if (!data?.file?.id) throw new Error('The server did not confirm the file was saved. Please try again.');
      setFiles(prev => [data.file, ...prev.filter(f => f.id !== data.file.id)]);
    } catch (err) {
      // Too large / a type it does not take: the server's own sentence names the limit, so it is shown.
      const said = typeof err?.body?.error === 'string' ? err.body.error : '';
      setFileFailure((err?.status === 413 || err?.status === 415) && said
        ? `Could not upload that file: ${said}`
        : describeWriteFailure(err, 'upload that file'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }

  // A link is kept on the project task itself (its attachments), through the same awaited save.
  async function addLink() {
    const name = linkName.trim();
    const url = linkUrl.trim();
    if (!name || !url) return;
    if (!/^https:\/\//i.test(url)) { setFileFailure('A link must start with https://'); return; }
    setSavingLink(true);
    setFileFailure('');
    const entry = { id: `l_${Date.now()}`, fileName: name, source: linkSource, externalUrl: url, addedAt: new Date().toISOString() };
    try {
      await projectManager.saveProjectTask(taskId, { attachments: [...links, entry] });
      setLinkName('');
      setLinkUrl('');
      setLinkingFile(false);
      onUpdated && onUpdated();
    } catch (err) {
      setFileFailure(describeWriteFailure(err, 'link that file'));
    } finally {
      setSavingLink(false);
      reload();
    }
  }

  // Removes a stored file (the file store deletes the bytes and logs it) or a link (from the task).
  async function removeAttachment(item) {
    setFileFailure('');
    try {
      if (isLink(item)) {
        await projectManager.saveProjectTask(taskId, { attachments: links.filter(l => l.id !== item.id) });
        reload();
        onUpdated && onUpdated();
      } else {
        await api(`/api/files/${encodeURIComponent(item.id)}`, { method: 'DELETE' });
        setFiles(prev => prev.filter(f => f.id !== item.id));
      }
    } catch (err) {
      // The row staying put is ambiguous on its own — it looks the same as a
      // delete that has not finished. The reason goes in the Files tab, where she
      // just pressed Remove.
      reload();
      setFileFailure(describeWriteFailure(err, 'remove that file'));
    }
  }

  // A stored file has no address of its own: a link that lives five minutes is asked for, after the
  // server has checked this person may see the task, and opened.
  async function openFile(f) {
    setFileFailure('');
    try {
      const res = await api(`/api/files/${encodeURIComponent(f.id)}/link`);
      const data = await res.json();
      if (!data?.url) throw new Error('That file could not be opened. Please try again.');
      window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      setFileFailure(messageFromError(err, 'That file could not be opened.'));
    }
  }

  if (!taskId || !task) return null;

  // One awaited change. It shows at once; a refusal puts it back and says why, here.
  const save = async (changes, id = taskId, what = 'save') => {
    setFailure('');
    setBusy(what);
    const pending = projectManager.saveProjectTask(id, changes);
    reload();
    try {
      await pending;
      onUpdated && onUpdated();
    } catch (err) {
      setFailure(messageFromError(err, 'That change was not saved.'));
    } finally {
      reload();
      setBusy('');
    }
  };

  const handleTitleSave = () => {
    const t = titleDraft.trim();
    if (!t) { setTitleDraft(task.title); return; }
    if (t !== task.title) save({ title: t });
  };

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (!text) return;
    const entry = { id: Date.now(), userId: currentUser?.id || null, comment: text, date: new Date().toISOString().split('T')[0], timestamp: Date.now() };
    await save({ comments: [...(task.comments || []), entry] }, taskId, 'comment');
    if (projectManager.getTaskById(taskId)?.comments?.some(c => c.id === entry.id)) setCommentText('');
  };

  const handleLogTime = async () => {
    const mins = parseInt(logMins, 10);
    if (!mins || mins < 1) return;
    const entry = { userId: currentUser?.id || null, minutes: mins, date: new Date().toISOString().split('T')[0], note: logNote.trim(), timestamp: Date.now() };
    await save({ timeTracked: [...(task.timeTracked || []), entry] }, taskId, 'time');
    if (projectManager.getTaskById(taskId)?.timeTracked?.some(e => e.timestamp === entry.timestamp)) { setLogMins(''); setLogNote(''); }
  };

  const handleCreateSubtask = async () => {
    if (!subtaskTitle.trim()) return;
    setFailure('');
    try {
      await projectManager.createSubtask(taskId, { title: subtaskTitle.trim(), priority: 'Medium', assignedTo: [] });
      setSubtaskTitle('');
      onUpdated && onUpdated();
    } catch (err) {
      setFailure(describeWriteFailure(err, 'add that subtask'));
    }
    reload();
  };

  const handleSubtaskToggle = (subId, done) => save({ status: done ? 'done' : 'todo' }, subId, 'subtask');

  const addTag = () => {
    const t = tagInput.trim().toLowerCase();
    if (!t || (task.tags || []).includes(t)) { setTagInput(''); return; }
    save({ tags: [...(task.tags || []), t] });
    setTagInput('');
  };

  const removeTag = (tag) => save({ tags: (task.tags || []).filter(x => x !== tag) });

  const toggleAssignee = (userId) => {
    const current = task.assignedTo || [];
    save({ assignedTo: current.includes(userId) ? current.filter(x => x !== userId) : [...current, userId] });
  };

  const totalTime = minutesOf(task);
  const project = projectManager.getProject(task.projectId);
  const status = task.status;
  const comments = task.comments || [];
  const entries = task.timeTracked || [];
  const links = (Array.isArray(task.attachments) ? task.attachments : []).filter(isLink);

  const tabs = [
    { id: 'details', label: 'Details', color: 'pastel-blue' },
    { id: 'subtasks', label: 'Subtasks', color: 'pastel-yellow', count: subtasks.length || undefined },
    { id: 'comments', label: 'Comments', color: 'pastel-pink', count: comments.length || undefined },
    { id: 'time', label: 'Time log', color: 'pastel-peach' },
    { id: 'files', label: 'Files', color: 'pastel-mint', count: (files.length + links.length) || undefined },
  ];

  return (
    <SidePanel open onClose={onClose} title={task.title || 'Task'} color={STATUS_TINT[status] || 'pastel-yellow'}
      actions={<Button variant="tertiary" onClick={onClose}>Close</Button>}>
      <div className="pj-drawer">
        {project && <p className="pj-muted">In {project.name}</p>}

        <Field label="Title">
          <Input value={titleDraft} onChange={e => setTitleDraft(e.target.value)} onBlur={handleTitleSave}
            onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setTitleDraft(task.title); }} />
        </Field>

        <div className="pj-drawer__grid">
          <Field label="Status">
            <Select value={status} onChange={e => save({ status: e.target.value })}
              options={[
                ...TASK_STATUSES.map(s => ({ value: s, label: STATUS_LABEL[s] })),
                // An unknown stored value still shows, so it is visible rather than hidden.
                ...(TASK_STATUSES.includes(status) ? [] : [{ value: status, label: String(status) }]),
              ]} />
          </Field>
          <Field label="Due date">
            <DateInput value={task.dueDate || ''} onChange={e => save({ dueDate: e.target.value })} />
          </Field>
        </div>

        <SegmentedControl label="Priority" options={PRIORITY_CHOICES} value={task.priority} onChange={(p) => save({ priority: p })} />

        <Field label="Story points">
          <Input type="number" min="0" inputMode="numeric" value={pointsDraft} onChange={e => setPointsDraft(e.target.value)}
            onBlur={() => { const n = parseInt(pointsDraft, 10) || 0; if (n !== (task.storyPoints || 0)) save({ storyPoints: n }); }} />
        </Field>

        {failure && <p className="pj-failed" role="alert">{failure}</p>}

        <TabGroup label="Task sections" idPrefix={`pj-task-${task.id}`} tabs={tabs} value={tab} onChange={setTab} />

        {tab === 'details' && (
          <div className="pj-drawer">
            <Field label="Description">
              <Textarea rows={4} value={descDraft} onChange={e => setDescDraft(e.target.value)} placeholder="Add a description…" />
            </Field>
            {descDraft !== (task.description || '') && (
              <div className="pj-row">
                <Button variant="confirm" size="sm" onClick={() => save({ description: descDraft })} disabled={!!busy}>Save description</Button>
                <Button variant="tertiary" size="sm" onClick={() => setDescDraft(task.description || '')}>Cancel</Button>
              </div>
            )}

            <div className="pj-drawer__section" role="group" aria-label="Assignees">
              <span className="pj-drawer__label">Assignees</span>
              {allUsers.length === 0 ? <p className="pj-muted">No one to assign yet.</p> : (
                <div className="pj-check-list">
                  {allUsers.map(u => (
                    <Checkbox key={u.id} label={u.name} checked={(task.assignedTo || []).includes(u.id)} onChange={() => toggleAssignee(u.id)} />
                  ))}
                </div>
              )}
            </div>

            <div className="pj-drawer__section">
              <span className="pj-drawer__label">Tags</span>
              {(task.tags || []).length > 0 && (
                <div className="pj-chips">
                  {(task.tags || []).map(tag => (
                    <span key={tag} className="pj-tag">{tag}<button type="button" aria-label={`Remove tag ${tag}`} onClick={() => removeTag(tag)}>×</button></span>
                  ))}
                </div>
              )}
              <div className="pj-inline">
                <Input aria-label="New tag" value={tagInput} onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') addTag(); }} placeholder="Add tag…" />
                <Button variant="secondary" onClick={addTag} disabled={!tagInput.trim()}>Add</Button>
              </div>
            </div>

            <div className="pj-tiles">
              <StatTile label="Time logged" value={formatMinutes(totalTime)} />
              <StatTile label="Story points" value={task.storyPoints || 0} />
              <StatTile label="Subtasks" value={subtasks.length} />
              <StatTile label="Comments" value={comments.length} />
            </div>
          </div>
        )}

        {tab === 'subtasks' && (
          <div className="pj-drawer">
            <div className="pj-inline">
              <Input aria-label="New subtask" value={subtaskTitle} onChange={e => setSubtaskTitle(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleCreateSubtask()} placeholder="Add subtask…" />
              <Button variant="confirm" onClick={handleCreateSubtask} disabled={!subtaskTitle.trim()}>Add</Button>
            </div>
            {subtasks.length === 0 ? (
              <EmptyState title="No subtasks yet">Break this task into smaller steps.</EmptyState>
            ) : (
              <ul className="pj-entries">
                {subtasks.map(sub => (
                  <li key={sub.id} className={`pj-entry${sub.status === 'done' ? ' pj-entry--done' : ''}`}>
                    <Checkbox label={sub.title} checked={sub.status === 'done'} onChange={(on) => handleSubtaskToggle(sub.id, on)} />
                    <span className="pj-spacer" />
                    <StatusPill tone={sub.status === 'done' ? 'success' : 'neutral'}>{STATUS_LABEL[sub.status] || sub.status}</StatusPill>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === 'comments' && (
          <div className="pj-drawer">
            <Field label="Comment">
              <Textarea rows={3} value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Write a comment…" />
            </Field>
            <div><Button variant="confirm" onClick={handleAddComment} disabled={!commentText.trim() || busy === 'comment'} aria-busy={busy === 'comment' || undefined}>
              {busy === 'comment' ? 'Posting…' : 'Post comment'}
            </Button></div>
            {comments.length === 0 ? (
              <EmptyState title="No comments yet">Comments here are seen by everyone on the project.</EmptyState>
            ) : (
              <ul className="pj-entries">
                {[...comments].reverse().map(c => {
                  const author = rosterMap.get(c.userId);
                  return (
                    <li key={c.id} className="pj-entry">
                      <Avatar name={author?.name || 'Former member'} size={36} color={personTint(c.userId)} />
                      <span className="pj-entry__text">
                        <span className="pj-entry__who">{author?.name || 'Former member'} <span className="pj-entry__when">{when(c)}</span></span>
                        <p className="pj-entry__body">{c.comment}</p>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === 'time' && (
          <div className="pj-drawer">
            <div className="pj-total"><span>Total logged</span><strong>{formatMinutes(totalTime)}</strong></div>
            <div className="pj-form">
              <div className="pj-drawer__grid">
                <Field label="Minutes"><Input type="number" min="1" inputMode="numeric" value={logMins} onChange={e => setLogMins(e.target.value)} placeholder="30" /></Field>
                <Field label="What did you work on?"><Input value={logNote} onChange={e => setLogNote(e.target.value)} placeholder="Optional" /></Field>
              </div>
              <div><Button variant="confirm" onClick={handleLogTime} disabled={!parseInt(logMins, 10) || busy === 'time'} aria-busy={busy === 'time' || undefined}>
                {busy === 'time' ? 'Logging…' : 'Log time'}
              </Button></div>
            </div>
            {entries.length === 0 ? (
              <EmptyState title="No time logged yet">Log what you spend on this task, and it adds up here.</EmptyState>
            ) : (
              <ul className="pj-entries">
                {[...entries].reverse().map((entry, i) => {
                  const u = rosterMap.get(entry.userId);
                  return (
                    <li key={entry.timestamp || i} className="pj-entry">
                      <Avatar name={u?.name || 'Former member'} size={36} color={personTint(entry.userId)} />
                      <span className="pj-entry__text">
                        <span className="pj-entry__who">{formatMinutes(entry.minutes)} <span className="pj-entry__when">{u?.name || 'Former member'} · {when(entry)}</span></span>
                        {entry.note && <p className="pj-entry__body">{entry.note}</p>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tab === 'files' && (
          <div className="pj-drawer">
            {fileFailure && (
              <p data-testid="file-failed" className="pj-failed" role="alert">{fileFailure}</p>
            )}
            {filesLoadFailure && <ErrorState title="Files did not load" message={filesLoadFailure} onRetry={loadFiles} />}
            {(files.length > 0 || links.length > 0) && (
              <ul className="pj-files">
                {files.map(f => (
                  <li key={f.id} className="pj-file">
                    <FileIcon mimeType={f.mimeType} />
                    <span className="pj-file__text">
                      <span className="pj-file__name" title={f.fileName}>{f.fileName}</span>
                      <span className="pj-file__meta">
                        <span>{sizeText(f.sizeBytes)}</span>
                        {f.createdAt && <span>{formatDate(f.createdAt, undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                      </span>
                    </span>
                    <Button size="sm" variant="tertiary" aria-label={`Open ${f.fileName}`} onClick={() => openFile(f)}>Open</Button>
                    <Button size="sm" variant="destructive" aria-label={`Remove ${f.fileName}`} onClick={() => removeAttachment(f)}>Remove</Button>
                  </li>
                ))}
                {links.map(a => (
                  <li key={a.id} className="pj-file">
                    <FileIcon source={a.source} />
                    <span className="pj-file__text">
                      <span className="pj-file__name" title={a.fileName}>{a.fileName}</span>
                      <span className="pj-file__meta"><StatusPill tone="neutral">{SOURCE_LABEL[a.source] || 'Link'}</StatusPill></span>
                    </span>
                    <a className="pj-file__link" href={a.externalUrl} target="_blank" rel="noopener noreferrer" aria-label={`Open ${a.fileName}`}>Open</a>
                    <Button size="sm" variant="destructive" aria-label={`Remove ${a.fileName}`} onClick={() => removeAttachment(a)}>Remove</Button>
                  </li>
                ))}
              </ul>
            )}
            {filesLoaded && !filesLoadFailure && files.length === 0 && links.length === 0 && !linkingFile && (
              <EmptyState title="No files yet">Upload a file, or link one from Google Drive, OneDrive or the web.</EmptyState>
            )}
            {linkingFile && (
              <Reveal className="pj-form">
                <Field label="Where it is">
                  <Select value={linkSource} onChange={e => setLinkSource(e.target.value)}
                    options={[{ value: 'google_drive', label: 'Google Drive' }, { value: 'onedrive', label: 'OneDrive' }, { value: 'url', label: 'Other link' }]} />
                </Field>
                <Field label="Name"><Input data-reveal-focus value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="e.g. Brand guidelines" /></Field>
                <Field label="Link" hint="It must start with https://"><Input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://…" /></Field>
                <div className="pj-row">
                  <Button variant="confirm" onClick={addLink} disabled={savingLink || !linkName.trim() || !linkUrl.trim()} aria-busy={savingLink || undefined}>{savingLink ? 'Linking…' : 'Link'}</Button>
                  <Button variant="tertiary" onClick={() => { setLinkingFile(false); setLinkName(''); setLinkUrl(''); }}>Cancel</Button>
                </div>
              </Reveal>
            )}
            <input ref={fileInputRef} type="file" accept={FILE_ACCEPT} hidden data-testid="task-file-input"
              onChange={e => uploadFile(e.target.files && e.target.files[0])} />
            {!linkingFile && (
              <>
                <div className="pj-row">
                  <Button variant="secondary" onClick={() => fileInputRef.current?.click()} disabled={uploading} aria-busy={uploading || undefined}>
                    {uploading ? 'Uploading…' : '+ Upload file'}
                  </Button>
                  <Button variant="tertiary" onClick={() => setLinkingFile(true)}>🔗 Link file</Button>
                </div>
                <p className="pj-muted">Up to 10 MB: images, PDF, Word, Excel, PowerPoint, text or CSV. Only people who can see this project can open them.</p>
              </>
            )}
          </div>
        )}
      </div>
    </SidePanel>
  );
}
